import { ApplicationRef, ComponentFactory, ComponentFactoryResolver, ComponentRef, Injectable, Injector, TemplateRef } from '@angular/core';
import { ModalWindow } from '../components/window/window.component';
import { ModalBackdrop } from '../components/backdrop/backdrop.component';
import { ModalRef } from '../classes/modal-ref';
import { ActiveModal } from '../classes/active-modal';
import { ContentRef } from '../classes/content-ref';
import { isNullOrUndefined, isString } from 'util';
import { ActiveModalButtons } from '../classes/active-modal-buttons';
import { ActiveModalLoader } from '../classes/active-modal-loader';

@Injectable()
export class ModalStackService {

    private backdropFactory: ComponentFactory<ModalBackdrop>;

    private windowFactory: ComponentFactory<ModalWindow>;

    /**
     * The z-index layer of the last modal.
     *
     * @type {number}
     */
    private layer = 1060;

    /**
     *
     * @param {ApplicationRef} applicationRef
     * @param {Injector} injector
     * @param {ComponentFactoryResolver} componentFactoryResolver
     */
    constructor(private applicationRef: ApplicationRef, private injector: Injector, private componentFactoryResolver: ComponentFactoryResolver) {
        this.backdropFactory = componentFactoryResolver.resolveComponentFactory(ModalBackdrop);
        this.windowFactory   = componentFactoryResolver.resolveComponentFactory(ModalWindow);
    }

    /**
     *
     * @param {ComponentFactoryResolver} componentFactoryResolver
     * @param {Injector} injector
     * @param content
     * @param data
     * @param options
     * @return {ModalRef}
     */
    public open(componentFactoryResolver: ComponentFactoryResolver, injector: Injector, content: any, data, options): ModalRef {
        const containerSelector = options.container || 'body';

        const containerElement = document.querySelector(containerSelector);
        if (!containerElement) {
            throw new Error(`The specified modal container "${containerSelector}" was not found in the DOM.`);
        }

        const activeModal = new ActiveModal();

        const activeModalButtons = new ActiveModalButtons();

        if (data) {
            this.applyActiveModalData(activeModal, data);
        }

        const contentRef = this.getContentRef(componentFactoryResolver, content, activeModal, activeModalButtons);

        let backdropComponentRef: ComponentRef<ModalBackdrop>;
        if (options.backdrop !== false) {
            backdropComponentRef = this.createBackdropRef(containerElement);
        }

        let windowComponentRef: ComponentRef<ModalWindow>;
        windowComponentRef = this.createWindowRef(containerElement, contentRef);

        let modalRef: ModalRef;
        modalRef = new ModalRef(windowComponentRef, contentRef, backdropComponentRef);

        // Bind the data properties directly to the modal instance
        // when the option is enabled.
        if (data && options.bindDataToInstance) {
            Object.assign(modalRef.instance, data);
        }

        this.applyActiveModalResultCallbacks(activeModal, modalRef);

        this.applyWindowAndBackdropOptions(windowComponentRef, backdropComponentRef, options);

        return modalRef;
    }

    /**
     *
     * @param {Element} containerElement
     * @return {ComponentRef<ModalBackdrop>}
     */
    private createBackdropRef(containerElement: Element) {
        const backdropComponentRef = this.backdropFactory.create(this.injector);

        this.applicationRef.attachView(backdropComponentRef.hostView);
        containerElement.appendChild(backdropComponentRef.location.nativeElement);

        return backdropComponentRef;
    }

    /**
     *
     * @param {Element} containerElement
     * @param {ContentRef} contentRef
     * @return {ComponentRef<ModalWindow>}
     */
    private createWindowRef(containerElement: Element, contentRef: ContentRef) {
        const windowComponentRef = this.windowFactory.create(this.injector, contentRef.nodes);

        this.applicationRef.attachView(windowComponentRef.hostView);
        containerElement.appendChild(windowComponentRef.location.nativeElement);

        return windowComponentRef;
    }

    /**
     *
     * @param {ActiveModal} activeModal
     * @param {ModalRef} modalRef
     */
    private applyActiveModalResultCallbacks(activeModal: ActiveModal, modalRef: ModalRef) {
        activeModal.close = (result: any) => {
            modalRef.close(result);
        };

        activeModal.dismiss = (reason: any) => {
            modalRef.dismiss(reason);
        };

        activeModal.beforeClose = (callback: () => void) => {
            modalRef.beforeClose(callback);
        };

        activeModal.beforeDismiss = (callback: () => void) => {
            modalRef.dismiss(callback);
        };
    }

    /**
     *
     * @param {ComponentRef<ModalWindow>} windowComponentRef
     * @param {ComponentRef<ModalBackdrop>} backdropComponentRef
     * @param options
     */
    private applyWindowAndBackdropOptions(windowComponentRef: ComponentRef<ModalWindow>, backdropComponentRef: ComponentRef<ModalBackdrop>, options: any) {
        options.zIndex = options.zIndex || this.layer;

        this.applyBackdropOptions(backdropComponentRef.instance, options);
        this.layer++;

        options.zIndex = options.zIndex ? (options.zIndex + 1) : this.layer;

        this.applyWindowOptions(windowComponentRef.instance, options);
        this.layer++;
    }

    /**
     *
     * @param {ModalWindow} windowInstance
     * @param {Object} options
     */
    private applyWindowOptions(windowInstance: ModalWindow, options: Object): void {
        ['backdrop', 'keyboard', 'size', 'classes', 'zIndex'].forEach((optionName: string) => {
            if (!isNullOrUndefined(options[optionName])) {
                if (['classes'].indexOf(optionName) >= 0) {
                    windowInstance[optionName] = (windowInstance[optionName] + ' ' + options[optionName]);
                } else {
                    windowInstance[optionName] = options[optionName];
                }
            }
        });
    }

    /**
     *
     * @param {ModalWindow} backdropInstance
     * @param {Object} options
     */
    private applyBackdropOptions(backdropInstance: ModalBackdrop, options: Object): void {
        ['zIndex'].forEach((optionName: string) => {
            if (!isNullOrUndefined(options[optionName])) {
                backdropInstance[optionName] = options[optionName];
            }
        });
    }

    /**
     *
     * @param {ActiveModal} activeModal
     * @param {Object} data
     */
    private applyActiveModalData(activeModal: ActiveModal, data: Object): void {
        Object.keys(data).forEach((property) => {
            if (!isNullOrUndefined(data[property])) {
                activeModal.set(property, data[property]);
            }
        });
    }

    /**
     *
     * @param {ComponentFactoryResolver} componentFactoryResolver
     * @param content
     * @param modalContext
     * @param buttonsContext
     * @return {ContentRef}
     */
    private getContentRef(componentFactoryResolver: ComponentFactoryResolver, content: any, modalContext: ActiveModal, buttonsContext: ActiveModalButtons): ContentRef {
        if (!content) {
            return new ContentRef([]);
        } else if (content instanceof TemplateRef) {
            return this.getContentRefOfTemplateRef(content, modalContext);
        } else if (isString(content)) {
            return this.getContentRefOfTextNode(content);
        } else {
            return this.getContentRefOfComponent(componentFactoryResolver, content, modalContext, buttonsContext);
        }
    }

    /**
     *
     * @param content
     * @param modalContext
     * @return {ContentRef}
     */
    private getContentRefOfTemplateRef(content, modalContext): ContentRef {
        const viewRef = content.createEmbeddedView(modalContext);

        this.applicationRef.attachView(viewRef);

        return new ContentRef([viewRef.rootNodes], viewRef);
    }

    /**
     *
     * @param content
     * @return {ContentRef}
     */
    private getContentRefOfTextNode(content): ContentRef {
        return new ContentRef([[document.createTextNode(`${content}`)]]);
    }

    /**
     *
     * @param componentFactoryResolver
     * @param content
     * @param modalContext
     * @param buttonsContext
     * @return {ContentRef}
     */
    private getContentRefOfComponent(componentFactoryResolver, content, modalContext: ActiveModal, buttonsContext: ActiveModalButtons): ContentRef {
        const contentComponentFactory = componentFactoryResolver.resolveComponentFactory(content);

        const loaderContext = new ActiveModalLoader(this.injector);

        const modalContentInjector = Injector.create({
            providers: [{
                provide: ActiveModal,
                useValue: modalContext
            }, {
                provide: ActiveModalButtons,
                useValue: buttonsContext
            }, {
                provide: ActiveModalLoader,
                useValue: loaderContext
            }],
        });

        const componentRef = contentComponentFactory.create(modalContentInjector);

        this.applicationRef.attachView(componentRef.hostView);

        return new ContentRef([[componentRef.location.nativeElement]], componentRef.hostView, componentRef);
    }

}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/modals/services/modal-stack.service.ts